import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import axios from 'axios';
import styled from 'styled-components';

function QuotationDetail() {
    const location = useLocation();
    const navigate = useNavigate();
    const printRef = useRef();

    const [quotation, setQuotation] = useState(null);
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const idx = location.state?.idx;
    const beforePage = location.state?.beforePage || '/quotation_list';

    const fetchQuotationDetail = async () => {
        try {
            setIsLoading(true);
            const result = await axios.get(`/Quotation/Detail/${idx}`);
            if (result.data.success) {
                const data = result.data.message;
                setQuotation(data);
                // items는 JSON 문자열로 저장되어 있으므로 파싱
                const parsedItems = typeof data.items === 'string'
                    ? JSON.parse(data.items)
                    : data.items || [];
                setItems(parsedItems);
            }
        } catch (error) {
            console.error('Error fetching quotation detail:', error.message);
            alert('견적서를 불러오는데 실패했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (idx) {
            fetchQuotationDetail();
        } else {
            alert('잘못된 접근입니다.');
            navigate('/quotation_list');
        }
    }, [idx]);

    const handleBack = () => {
        navigate(beforePage);
    };

    const handleSaveAsPDF = async () => {
        const input = printRef.current;
        if (!input) return;

        const canvas = await html2canvas(input);
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgWidth = 210;
        const imgProps = pdf.getImageProperties(imgData);
        const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
        pdf.save(`quotation_${quotation?.quotation_number || 'document'}.pdf`);
    };

    const calcSupply = (item) => item.quantity * item.price;
    const calcTax = (item) => calcSupply(item) * 0.1;

    const totalSupply = items.reduce((sum, item) => sum + calcSupply(item), 0);
    const totalTax = items.reduce((sum, item) => sum + calcTax(item), 0);
    const totalAmount = totalSupply + totalTax;

    // 스타일 정의 (quotation.js와 동일)
    const containerStyle = { width: '800px', margin: '50px auto', backgroundColor: '#fff', padding: '20px', fontFamily: 'sans-serif', position: 'relative' };
    const containerBottomStyle = { width: '800px', padding: '20px', margin: '0px auto', fontFamily: 'sans-serif', position: 'relative', textAlign: 'right' };
    const tableStyle = { width: '100%', borderCollapse: 'collapse', marginTop: '20px', border: '3px solid #000', lineHeight: '2' };
    const thStyle = { border: '1px solid #000', textAlign: 'center', padding: '5px', backgroundColor: '#f9f9f9' };
    const tdStyle = { border: '1px solid #000', padding: '5px', textAlign: 'center' };
    const numberTdStyle = { border: '1px solid #000', textAlign: 'right', padding: '5px' };
    const titleStyle = { textAlign: 'center', fontSize: '50px', fontWeight: 'bold', marginBottom: '10px' };

    if (isLoading) {
        return (
            <div className="dashboard-container">
                <div className="content-container">
                    <LoadingMessage>로딩 중...</LoadingMessage>
                </div>
            </div>
        );
    }

    if (!quotation) {
        return (
            <div className="dashboard-container">
                <div className="content-container">
                    <ErrorMessage>견적서를 찾을 수 없습니다.</ErrorMessage>
                    <BackButton onClick={handleBack}>목록으로</BackButton>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-container">
            <div className="content-container">
                <div>
                    <div style={containerStyle} ref={printRef}>
                        <div style={titleStyle}>견 적 서</div>
                        <div style={{ fontSize: '15px', textAlign: 'right' }}>{quotation.quotation_number}</div>
                        <table style={tableStyle}>
                            <tbody>
                                <tr>
                                    <td style={tdStyle} rowSpan="5">
                                        {quotation.created_date}<br />
                                        {quotation.company_name ? `${quotation.company_name} 귀하.` : '귀하.'}<br />
                                        아래와 같이 견적합니다.
                                    </td>
                                    <td style={tdStyle} rowSpan="5">공급자</td>
                                    <td style={tdStyle}>등록번호</td>
                                    <td style={tdStyle} colSpan="3">614-86-03192</td>
                                </tr>
                                <tr>
                                    <td style={tdStyle}>상호</td>
                                    <td style={tdStyle}>에어컨알지</td>
                                    <td style={tdStyle}>대표자</td>
                                    <td style={tdStyle}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                                            <span>조철호</span>
                                            <img src="/stamp.png" alt="도장" style={{ width: '40px', height: '40px', marginLeft: '-5px' }} />
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={tdStyle}>사업장주소</td>
                                    <td style={tdStyle} colSpan="3">{quotation.address}</td>
                                </tr>
                                <tr>
                                    <td style={tdStyle}>업태</td>
                                    <td style={tdStyle}>서비스</td>
                                    <td style={tdStyle}>종목</td>
                                    <td style={tdStyle}>{quotation.jongmok}</td>
                                </tr>
                                <tr>
                                    <td style={tdStyle}>대표번호</td>
                                    <td style={tdStyle}>1688-2637</td>
                                    <td style={tdStyle}>FAX</td>
                                    <td style={tdStyle}>031-745-7771</td>
                                </tr>
                                <tr>
                                    <td style={tdStyle} colSpan="6">
                                        금 액 : 영 원정 (₩ {totalAmount.toLocaleString()}) VAT 포함.
                                    </td>
                                </tr>

                                <tr>
                                    <th style={thStyle}>품 명</th>
                                    <th style={thStyle}>규 격</th>
                                    <th style={thStyle}>수 량</th>
                                    <th style={thStyle}>단 가</th>
                                    <th style={thStyle}>공급가액</th>
                                    <th style={thStyle}>세 액</th>
                                </tr>

                                {items.map((item, idx) => {
                                    const supply = calcSupply(item);
                                    const tax = calcTax(item);
                                    return (
                                        <tr key={idx}>
                                            <td style={tdStyle}>{item.name}</td>
                                            <td style={tdStyle}>{item.spec}</td>
                                            <td style={numberTdStyle}>{item.quantity}</td>
                                            <td style={numberTdStyle}>{item.price?.toLocaleString()}</td>
                                            <td style={numberTdStyle}>{supply.toLocaleString()}</td>
                                            <td style={numberTdStyle}>{tax.toLocaleString()}</td>
                                        </tr>
                                    );
                                })}
                                <tr>
                                    <td style={tdStyle} colSpan="4" align="center">합 계</td>
                                    <th style={numberTdStyle}>{totalSupply.toLocaleString()}</th>
                                    <th style={numberTdStyle}>{totalTax.toLocaleString()}</th>
                                </tr>
                                <tr>
                                    <td style={{ marginTop: '20px', textAlign: 'left', lineHeight: '2', whiteSpace: 'pre-wrap' }} colSpan="6">
                                        {quotation.bigo}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div style={containerBottomStyle}>
                        <BackButton onClick={handleBack}>
                            목록으로
                        </BackButton>
                        <PdfButton onClick={handleSaveAsPDF}>
                            PDF 저장
                        </PdfButton>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default QuotationDetail;

const LoadingMessage = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 300px;
    font-size: 1.2rem;
    color: #666;
`;

const ErrorMessage = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
    font-size: 1.2rem;
    color: #d32f2f;
`;

const BackButton = styled.button`
    padding: 10px 20px;
    width: 120px;
    background-color: #6c757d;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    margin-right: 10px;
    transition: all 0.2s;

    &:hover {
        background-color: #5a6268;
    }
`;

const PdfButton = styled.button`
    padding: 10px 20px;
    width: 120px;
    background-color: #006edc;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background-color: #0056b3;
    }
`;
